/**
 * compactSummary — derives a one-line statement summary for the Compact VQE
 * popover header (e.g. `SELECT from customers (c)`, `UPDATE orders`).
 *
 * Pure logic over a `QueryStructure` so it's unit-testable without React. The
 * structure comes from `service.extractQueryStructure(...)`; callers pass the
 * already-extracted structure (or `null` when extraction failed) plus the
 * `RenderMode` so the verb is correct even when no FROM/target resolves.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS3.
 */

import type {
  FromItem,
  FromItemTarget,
  QueryStructure,
} from '../../../sql-resolver/types/queryStructure';
import type { RenderMode } from '../types';
import {
  type DmlAliasCandidate,
  formatRelationAlias,
  resolveDmlTargetAlias,
} from '../render/update/resolveDmlTargetAlias';

const VERB_BY_MODE: Partial<Record<RenderMode, string>> = {
  select: 'SELECT',
  insert: 'INSERT',
  update: 'UPDATE',
  delete: 'DELETE',
  merge: 'MERGE',
};

/** The relation/CTE/routine name a FROM-item target names, or `null` for kinds with no simple name. */
function relationNameOf(target: FromItemTarget): string | null {
  if (target.kind === 'relation') return target.qualified.name;
  if (target.kind === 'cte-ref') return target.cte.name.name;
  if (target.kind === 'function-call') return target.routine.name;
  if (target.kind === 'derived-table') return '(subquery)';
  return null;
}

/** Render the primary relation + optional alias for a FROM/target item. */
function describeFromItem(item: FromItem | undefined): string | null {
  if (!item) return null;
  const base = relationNameOf(item.target);
  if (!base) return null;
  return formatRelationAlias(base, item.alias?.alias ?? null);
}

/**
 * DML-14/15 parity: an UPDATE/DELETE/MERGE target read straight off its own
 * clause (`structure.updateTarget` etc.) sometimes carries no alias of its
 * own because the real one lives in a separate FROM clause the target
 * reader never sees — mssql `UPDATE p SET … FROM Production.Product p …`.
 * MySQL/Oracle's target reader already captures a trailing bare alias
 * directly, so this only kicks in for the mssql shape; it's a no-op
 * (returns `describeFromItem(primary)` unchanged) whenever the primary
 * target already has its own alias or there's no FROM tail to search.
 */
function describeDmlTarget(
  primary: FromItem | undefined,
  fromItems: readonly FromItem[] | undefined,
): string | null {
  if (!primary) return null;
  if (primary.alias?.alias) return describeFromItem(primary);
  const base = relationNameOf(primary.target);
  if (!base) return null;
  if (!fromItems || fromItems.length === 0) return base;

  const candidates: readonly DmlAliasCandidate[] = fromItems.map((item) => ({
    relation: relationNameOf(item.target),
    alias: item.alias?.alias ?? null,
  }));
  const resolved = resolveDmlTargetAlias(base, candidates);
  return resolved.relation ? formatRelationAlias(resolved.relation, resolved.alias) : base;
}

/**
 * EDG-26: MERGE-only alias formatting. `describeDmlTarget` (used by
 * UPDATE/DELETE) formats a resolved alias via the shared bare-parens
 * `formatRelationAlias` ("orders (o)") — fine next to an UPDATE/DELETE verb,
 * but "MERGE employees (t)" reads like a mystery count parenthetical rather
 * than an alias. MERGE gets a labeled `AS` join instead ("employees AS t");
 * UPDATE/DELETE/SELECT/INSERT keep the existing bare-parens format
 * unchanged (locked by `compactSummary.test.ts`).
 */
function describeMergeTarget(
  primary: FromItem | undefined,
  fromItems: readonly FromItem[] | undefined,
): string | null {
  if (!primary) return null;
  const base = relationNameOf(primary.target);
  if (!base) return null;
  if (primary.alias?.alias) return formatWithLabeledAlias(base, primary.alias.alias);
  if (!fromItems || fromItems.length === 0) return base;

  const candidates: readonly DmlAliasCandidate[] = fromItems.map((item) => ({
    relation: relationNameOf(item.target),
    alias: item.alias?.alias ?? null,
  }));
  const resolved = resolveDmlTargetAlias(base, candidates);
  if (!resolved.relation) return base;
  return formatWithLabeledAlias(resolved.relation, resolved.alias);
}

/** `relation AS alias` when the alias adds information, else just `relation`. */
function formatWithLabeledAlias(relation: string, alias: string | null): string {
  return alias && alias.trim().toLowerCase() !== relation.trim().toLowerCase()
    ? `${relation} AS ${alias}`
    : relation;
}

/**
 * Build the header summary. Falls back to just the verb when no relation
 * resolves (e.g. snapshot not loaded yet) so the header is never blank.
 */
export function buildCompactSummary(
  mode: RenderMode | null,
  structure: QueryStructure | null,
): string {
  const verb = (mode && VERB_BY_MODE[mode]) ?? 'Edit';
  if (!structure) return verb;

  let relation: string | null;
  switch (structure.kind) {
    case 'select':
      relation = describeFromItem(structure.fromItems?.[0]);
      break;
    case 'insert':
      relation = describeFromItem(structure.insertTarget);
      break;
    case 'update':
      relation = describeDmlTarget(structure.updateTarget, structure.fromItems);
      break;
    case 'delete':
      relation = describeDmlTarget(structure.deleteTarget, structure.fromItems);
      break;
    case 'merge':
      relation = describeMergeTarget(structure.mergeTarget, structure.fromItems);
      break;
    default:
      relation = null;
  }

  if (!relation) return verb;
  // "SELECT from customers (c)" / "UPDATE orders" / "MERGE INTO employees AS t".
  const connector = structure.kind === 'select' ? ' from ' : structure.kind === 'merge' ? ' INTO ' : ' ';
  return `${verb}${connector}${relation}`;
}
